import * as React from "react"
import { AlertCircle } from "lucide-react"
import {
  ConfigPropertiesPanel,
  type ConfigProperty,
  type ConfigPropertyValue,
  type EnumOption,
} from "@/components/alfred/config-properties-panel"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import {
  OPERATING_MODE_OPTIONS,
  type AppleVendor,
  type DisplayConfigurationItem,
  type GoogleVendor,
  type AlfredGlobalConfigKey,
  type ProVenueInstance,
  type ProVenueInstanceInput,
} from "@/types/alfred-config"
import {
  ADMIN_IDLE_OPTIONS,
  DISPLAY_TIME_OPTIONS,
  OFFLINE_BATCH_OPTIONS,
  SOUND_INVALID_OPTIONS, SOUND_VALID_OPTIONS,
  SOUND_WARNING_OPTIONS, TURNSTILE_MODE_OPTIONS, UNLOCKED_ACCESS_OPTIONS,
  VALIDATION_VENDOR_OPTIONS,
} from "@/components/alfred/alfred-device-form-fields"
import type { AlfredDeviceConfig, AlfredDeviceConfigKey, UpdateAlfredDeviceRequest } from "@/types/alfred-device"
import type { CreateAlfredDeviceRequest } from "@/types/alfred-device"
import type { AlfredClientConfig } from "@/types/alfred-client"
import type { AlfredGlobalConfig } from "@/types/alfred-config"
import { DeviceNotFoundState } from "@/components/shared/empty-states"
import { useAlfredDevice, useUpdateAlfredDeviceById } from "@/hooks/use-alfred-devices"
import { useAlfredGlobalConfig } from "@/hooks/use-alfred-config"
import { isNotFoundError } from "@/types/api"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import {
  presentAlfredConfigFields,
} from "@/components/alfred/alfred-device-field-presentation"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"


type FieldDescriptor =
  | { key: AlfredDeviceConfigKey; type: "readonly" }
  | { key: AlfredDeviceConfigKey; type: "string"; placeholder?: string }
  | { key: AlfredDeviceConfigKey; type: "number"; min?: number; max?: number; step?: number }
  | { key: AlfredDeviceConfigKey; type: "boolean" }
  | { key: AlfredDeviceConfigKey; type: "string-array" }
  | {
      key: AlfredDeviceConfigKey
      type: "json"
      itemLabel?: string
      getItemPreviewLabel?: (item: unknown) => string
      editor?: (
        value: unknown[],
        onChange: (next: unknown[]) => void,
        disabled: boolean,
        accessibleLabel: string,
        usesGlobalDefault: boolean,
      ) => React.ReactNode
    }
  | { key: AlfredDeviceConfigKey; type: "enum"; options: ReadonlyArray<EnumOption>; numeric?: boolean }
  | {
      key: "configurationPassword" | "ticketmasterConsumerKey"
      type: "secret"
      htmlPasswordInput?: boolean
      placeholder?: string
    }

const BASE_FIELD_DESCRIPTORS: FieldDescriptor[] = [
  { key: "deviceId", type: "readonly" },
  { key: "clientId", type: "readonly" },
  { key: "sotiDeviceId", type: "readonly" },
  { key: "venueCode", type: "readonly" },
  { key: "validationVendor", type: "enum", options: VALIDATION_VENDOR_OPTIONS },
  { key: "proVenueUser", type: "string" },
  {
    key: "proVenueInstances",
    type: "json",
    itemLabel: "instance",
    getItemPreviewLabel: (item) => {
      const instance = item as ProVenueInstanceInput
      return instance.name || instance.hostName || "Unnamed instance"
    },
    editor: (value, onChange, disabled, accessibleLabel, usesGlobalDefault) => (
      <ProVenueInstanceEditor
        items={value as ProVenueInstanceInput[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
        usesGlobalDefault={usesGlobalDefault}
      />
    ),
  },
  { key: "facilityCode", type: "string" },
  { key: "league", type: "string" },
  { key: "areaCode", type: "string" },
  { key: "deviceCode", type: "string" },
  { key: "offlineValidationMode", type: "boolean" },
  { key: "offlineBatchUploadLimit", type: "enum", options: OFFLINE_BATCH_OPTIONS, numeric: true },
  { key: "soundValidNumber", type: "enum", options: SOUND_VALID_OPTIONS, numeric: true },
  { key: "soundInvalidNumber", type: "enum", options: SOUND_INVALID_OPTIONS, numeric: true },
  { key: "soundWarningNumber", type: "enum", options: SOUND_WARNING_OPTIONS, numeric: true },
  { key: "maxCpuTemperature", type: "number" },
  { key: "minCpuTemperature", type: "number" },
  { key: "exitScanningAllowed", type: "boolean" },
  { key: "adminModeIdleWindow", type: "enum", options: ADMIN_IDLE_OPTIONS, numeric: true },
  { key: "validDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "invalidDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "operatingMode", type: "enum", options: OPERATING_MODE_OPTIONS },
  { key: "turnstileMode", type: "enum", options: TURNSTILE_MODE_OPTIONS, numeric: true },
  { key: "unlockedAccessTime", type: "enum", options: UNLOCKED_ACCESS_OPTIONS, numeric: true },
  { key: "isLockFailSafe", type: "boolean" },
  { key: "useSimpleResponse", type: "boolean" },
  { key: "mainBackgroundColor", type: "string" },
  { key: "secondaryBackgroundColor", type: "string" },
  { key: "idleStateColor", type: "string" },
  { key: "nfcEnabled", type: "boolean" },
  { key: "enableFan", type: "boolean" },
  { key: "enableHotspot", type: "boolean" },
  { key: "wakeLockEnabled", type: "boolean" },
  { key: "enableScanReset", type: "boolean" },
  { key: "enableManualValidationMask", type: "boolean" },
  {
    key: "displayConfiguration",
    type: "json",
    itemLabel: "screen",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <DisplayConfigurationEditor
        items={value as DisplayConfigurationItem[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "appleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <AppleVendorEditor
        items={value as AppleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "googleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <GoogleVendorEditor
        items={value as GoogleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  { key: "ticketmasterVenueId", type: "string" },
  {
    key: "ticketmasterConsumerKey",
    type: "secret",
    placeholder: "Leave blank to keep current",
  },
  {
    key: "configurationPassword",
    type: "secret",
    htmlPasswordInput: true,
    placeholder: "Leave blank to keep current",
  },
]

const FIELD_DESCRIPTORS = presentAlfredConfigFields(BASE_FIELD_DESCRIPTORS, {
  requireCompleteSchema: true,
})

/** Fingerprint of the resolved device config payload (includes inherited global values). */
function deviceConfigResetKey(device: AlfredDeviceConfig): string {
  return JSON.stringify(device)
}

function configToProperties(
  config: AlfredDeviceConfig,
  globalConfig?: AlfredGlobalConfig,
): ConfigProperty[] {
  const inherited = new Set(config.inheritedFromGlobal)
  return FIELD_DESCRIPTORS.map((field): ConfigProperty => {
    if (field.type === "secret") {
      return {
        label: field.label,
        section: field.section,
        key: field.key,
        type: "string",
        value: "",
        password: field.htmlPasswordInput,
        maskedCredential: !field.htmlPasswordInput,
        isInherited: inherited.has(field.key),
        placeholder: field.placeholder ?? "Leave blank to keep current",
      }
    }
    const raw = config[field.key]
    const globalRaw = globalConfig?.[field.key as AlfredGlobalConfigKey]
    const isInherited = inherited.has(field.key)
    switch (field.type) {
      case "readonly":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "readonly",
          value: String(raw ?? ""),
        }
      case "string":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string",
          value: (raw as string | undefined) ?? "",
          defaultValue: globalRaw as string | undefined,
          isInherited,
          placeholder: field.placeholder,
        }
      case "number":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "number",
          value: raw as number,
          defaultValue: globalRaw as number | undefined,
          isInherited,
          min: field.min,
          max: field.max,
          step: field.step,
        }
      case "boolean":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "boolean",
          value: raw as boolean,
          defaultValue: globalRaw as boolean | undefined,
          isInherited,
        }
      case "string-array":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string-array",
          value: raw as unknown as string[],
          defaultValue: globalRaw as string[] | undefined,
          isInherited,
        }
      case "json": {
        // ProVenue instances arrive with only non-sensitive metadata. Seed the
        // editor's working value with blank write-only credentials so entered
        // secrets persist across sheet reopens but are never sourced from the API.
        const toJsonValue = (value: unknown): unknown[] =>
          field.key === "proVenueInstances"
            ? ((value as ProVenueInstance[] | undefined) ?? []).map((instance) => ({
                ...instance,
                consumerKey: "",
                consumerSecret: "",
              }))
            : ((value as unknown[] | undefined) ?? [])
        const jsonValue = toJsonValue(raw)
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "json",
          value: jsonValue as unknown[],
          defaultValue: globalRaw === undefined ? undefined : toJsonValue(globalRaw),
          isInherited,
          itemLabel: field.itemLabel,
          getItemPreviewLabel: field.getItemPreviewLabel,
          replaceInheritedAsCollection: [
            "proVenueInstances",
            "appleVendors",
            "googleVendors",
          ].includes(field.key),
          startEmptyOnDefaultOverride: field.key === "proVenueInstances",
          editor: field.editor,
        }
      }
      case "enum":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "enum",
          value: raw as string | number,
          defaultValue: globalRaw as string | number | undefined,
          isInherited,
          options: field.options,
        }
    }
  })
}

function propertiesToUpdate(next: Record<string, ConfigPropertyValue | null>): UpdateAlfredDeviceRequest {
  const update: Record<string, ConfigPropertyValue | null> = {}
  for (const field of FIELD_DESCRIPTORS) {
    if (field.type === "readonly") continue
    const v = next[field.key]
    if (v === undefined) continue
    if (field.type === "secret") {
      if (v === null) {
        ;(update as Record<string, unknown>)[field.key] = { value: null, useDefault: true }
      } else if (typeof v === "string" && v !== "") {
        ;(update as Record<string, unknown>)[field.key] = { value: v, useDefault: false }
      }
      // empty string = no change, omit
      continue
    }
    if (v === null) { update[field.key] = null; continue }
    if (field.type === "number" && v === "") continue
    if (field.type === "enum" && field.numeric && typeof v === "string") {
      update[field.key] = Number(v)
      continue
    }
    update[field.key] = v
  }
  return update as UpdateAlfredDeviceRequest
}

function requiredText(label: string, value: ConfigPropertyValue): string | undefined {
  return typeof value === "string" && value.trim() ? undefined : `${label} is required`
}

function createConfigToProperties(
  globalConfig: AlfredGlobalConfig,
  clients: AlfredClientConfig[],
  defaultClientId?: string,
): ConfigProperty[] {
  const inheritedKeys = new Set(
    FIELD_DESCRIPTORS.filter((field) => field.type !== "readonly").map((field) => field.key),
  )
  const config = {
    ...globalConfig,
    id: 0,
    deviceId: "",
    clientId: defaultClientId ?? "",
    sotiDeviceId: "",
    venueCode: "",
    inheritedFromGlobal: [...inheritedKeys],
    createdAt: "",
    updatedAt: "",
    updatedBy: "",
  } as AlfredDeviceConfig

  return configToProperties(config, globalConfig).flatMap((property): ConfigProperty[] => {
    if (property.key === "venueCode") return []
    if (property.key === "deviceId") {
      return [{
        label: property.label,
        section: property.section,
        key: property.key,
        type: "string",
        value: "",
        allowDefault: false,
        required: true,
        placeholder: "e.g., LAD-GATE-001",
        characterLimit: MAX_ALFRED_INDEXED_STRING_LENGTH,
        validate: (value) => {
          const required = requiredText("Device ID", value)
          if (required) return required
          const text = value as string
          return getAlfredMaxLengthError("Device ID", text) ??
            (/^[A-Za-z0-9-_]+$/.test(text)
              ? undefined
              : "Device ID can only contain letters, numbers, hyphens, and underscores")
        },
      }]
    }
    if (property.key === "clientId") {
      return [{
        label: property.label,
        section: property.section,
        key: property.key,
        type: "enum",
        value: defaultClientId ?? "",
        allowDefault: false,
        required: true,
        placeholder: "Select a client...",
        options: clients.map((client) => ({ value: client.clientId, label: client.clientId })),
        validate: (value) =>
          typeof value === "string" && clients.some((client) => client.clientId === value)
            ? undefined
            : "Client is required",
      }]
    }
    if (property.key === "sotiDeviceId") {
      return [{
        label: property.label,
        section: property.section,
        key: property.key,
        type: "string",
        value: "",
        allowDefault: false,
        required: true,
        placeholder: "e.g., soti-lad-001",
        validate: (value) => requiredText("SOTI Device ID", value),
      }]
    }
    if (property.key === "configurationPassword" && property.type === "string") {
      return [{
        ...property,
        placeholder: "Enter a configuration password",
        validate: (value) => requiredText("Configuration password", value),
      }]
    }
    if (property.key === "ticketmasterConsumerKey" && property.type === "string") {
      return [{
        ...property,
        placeholder: "Enter a Ticketmaster consumer key",
        validate: (value) => requiredText("Ticketmaster consumer key", value),
      }]
    }
    if (property.key === "appleVendors" && property.type === "json") {
      return [{
        ...property,
        editor: (value, onChange, disabled, accessibleLabel) => (
          <AppleVendorEditor
            items={value as AppleVendor[]}
            onChange={(next) => onChange(next as unknown[])}
            disabled={disabled}
            requireSecret
            triggerAriaLabel={accessibleLabel}
          />
        ),
      }]
    }
    if (property.key === "googleVendors" && property.type === "json") {
      return [{
        ...property,
        editor: (value, onChange, disabled, accessibleLabel) => (
          <GoogleVendorEditor
            items={value as GoogleVendor[]}
            onChange={(next) => onChange(next as unknown[])}
            disabled={disabled}
            requireSecret
            triggerAriaLabel={accessibleLabel}
          />
        ),
      }]
    }
    return [property]
  })
}

function propertiesToCreate(
  next: Record<string, ConfigPropertyValue | null>,
  clients: AlfredClientConfig[],
): CreateAlfredDeviceRequest {
  const clientId = String(next.clientId ?? "")
  const client = clients.find((candidate) => candidate.clientId === clientId)
  if (!client) throw new Error("Select a valid client")

  const request: CreateAlfredDeviceRequest = {
    deviceId: String(next.deviceId ?? ""),
    clientId: client.id,
    sotiDeviceId: String(next.sotiDeviceId ?? ""),
  }

  for (const field of FIELD_DESCRIPTORS) {
    if (["deviceId", "clientId", "sotiDeviceId", "venueCode"].includes(field.key)) continue
    const value = next[field.key]
    if (value === undefined || value === null || value === "") continue
    if (field.type === "enum" && field.numeric && typeof value === "string") {
      ;(request as unknown as Record<string, unknown>)[field.key] = Number(value)
      continue
    }
    ;(request as unknown as Record<string, unknown>)[field.key] = value
  }

  return request
}

interface AlfredDeviceConfigViewProps {
  id: number
  defaultEditing?: boolean
  onBackToDevices: () => void
  backToDevicesLabel?: string
}

export function AlfredDeviceConfigView({
  id,
  defaultEditing,
  onBackToDevices,
  backToDevicesLabel = "Back to Devices",
}: AlfredDeviceConfigViewProps) {
  const { toast } = useToast()
  const { data: device, error, isLoading, mutate } = useAlfredDevice(id)
  const { data: globalConfig } = useAlfredGlobalConfig()
  const update = useUpdateAlfredDeviceById(id)

  if (isLoading) return <PropertiesSkeleton />
  if (error && isNotFoundError(error)) {
    return (
      <DeviceNotFoundState
        onBack={onBackToDevices}
        actionLabel={backToDevicesLabel}
      />
    )
  }
  if (error || !device) return <LoadErrorState onRetry={() => mutate()} />

  const properties = configToProperties(device, globalConfig)

  const handleSave = async (next: Record<string, ConfigPropertyValue | null>) => {
    try {
      const patch = propertiesToUpdate(next)
      const updated = await update.trigger(patch, { revalidate: false })
      if (updated) await mutate(updated, { revalidate: false })
      toast({ variant: "success", title: "Device configuration updated" })
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  return (
    <ConfigPropertiesPanel
      properties={properties}
      resetKey={deviceConfigResetKey(device)}
      onSave={handleSave}
      isSaving={update.isMutating}
      defaultEditing={defaultEditing}
      saveConfirmationTitle="Save device configuration changes?"
      saveConfirmationDescription="Are you sure you want to save these changes to this device?"
    />
  )
}

interface AlfredDeviceCreateViewProps {
  clients: AlfredClientConfig[]
  globalConfig: AlfredGlobalConfig
  defaultClientId?: string
  onCreate: (request: CreateAlfredDeviceRequest) => Promise<void>
  isCreating?: boolean
}

export function AlfredDeviceCreateView({
  clients,
  globalConfig,
  defaultClientId,
  onCreate,
  isCreating = false,
}: AlfredDeviceCreateViewProps) {
  const properties = React.useMemo(
    () => createConfigToProperties(globalConfig, clients, defaultClientId),
    [clients, defaultClientId, globalConfig],
  )

  const handleCreate = async (next: Record<string, ConfigPropertyValue | null>) => {
    await onCreate(propertiesToCreate(next, clients))
  }

  return (
    <ConfigPropertiesPanel
      properties={properties}
      resetKey={JSON.stringify([globalConfig, defaultClientId])}
      onSave={handleCreate}
      isSaving={isCreating}
      defaultEditing
      editingLocked
      configurationTitle="New device configuration"
      saveConfirmationTitle="Create this device?"
      saveConfirmationDescription="The device will be created with the selected overrides and all remaining values inherited from global defaults."
      submitLabel="Create Device"
      savingLabel="Creating..."
      confirmationActionLabel="Create Device"
      confirmSave={false}
    />
  )
}

function PropertiesSkeleton() {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6 space-y-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-6">
            <Skeleton className="h-4 w-1/3 bg-muted" />
            <Skeleton className="h-9 flex-1 max-w-xs bg-muted" />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function LoadErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Failed to load device config</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          The device configuration could not be loaded. Please try again.
        </p>
        <Button onClick={onRetry} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          Retry
        </Button>
      </CardContent>
    </Card>
  )
}
